import React, { useState, useEffect } from 'react';
import { Zap, Menu, X, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface HeaderProps {
  isDark: boolean;
  toggleTheme: () => void;
  onNavClick: (name: string, href: string, e?: React.MouseEvent) => void;
  currentView: string;
}

const Header: React.FC<HeaderProps> = ({ isDark, toggleTheme, onNavClick, currentView }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Service', href: '/service' },
    // { name: 'Blogs', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, linkName: string, linkHref: string) => {
    e.preventDefault();
    onNavClick(linkName, linkHref, e);
    setMobileMenuOpen(false);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    onNavClick('Contact', '/contact', e);
    setMobileMenuOpen(false);
  };

  const [serviceHovered, setServiceHovered] = useState(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setServiceHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setServiceHovered(false);
    }, 200);
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-0 ${scrolled 
        ? 'bg-white/40 dark:bg-black/40 backdrop-blur-lg border-b border-gray-200 dark:border-white/5 py-4' 
        : 'bg-transparent py-6'
        }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a 
          href="/" 
          className="flex items-center gap-2 group"
          onClick={(e) => handleLinkClick(e, 'Home', '/')}
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-red to-red-900 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-[0_0_15px_rgba(255,59,59,0.3)]">
            <Zap className="w-4 h-4 text-white fill-white" />
          </div>
          <span className="font-bold text-lg tracking-wide text-gray-900 dark:text-white transition-colors">Neotronics</span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            if (link.name === 'Service') {
              return (
                <div 
                  key={link.name} 
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <a
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link.name, link.href)}
                    className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                      (link.name.toLowerCase() === currentView || (link.name === 'Service' && currentView === 'service'))
                        ? 'text-brand-red' 
                        : 'text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white'
                    }`}
                  >
                    {link.name}
                  </a>
                  
                  {/* Submenu */}
                  <AnimatePresence>
                    {serviceHovered && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, visibility: 'hidden' }}
                        animate={{ opacity: 1, y: 0, visibility: 'visible' }}
                        exit={{ opacity: 0, y: 10, visibility: 'hidden' }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 top-full w-screen pt-4 flex justify-center -ml-[50vw] left-1/2"
                      >
                        <div className="bg-white dark:bg-[#111] border-t border-gray-200 dark:border-white/10 shadow-2xl w-full max-w-7xl mx-auto rounded-b-3xl overflow-hidden flex flex-col">
                          {/* Top Section: Grid of links */}
                          <div className="p-10 grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-10">
                            {/* Column 1 */}
                            <div className="flex flex-col gap-4">
                              <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mb-2">AI IPBX</h4>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">EPABX</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">IP Phones</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">IP PBX</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">FXS Gateway</a>
                            </div>
                            {/* Column 2 */}
                            <div className="flex flex-col gap-4">
                              <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mb-2">Cloud Telephony</h4>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Cloud PBX</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Mobile Call Analytics</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Multi-Cloud Networking</a>
                            </div>
                            {/* Column 3 */}
                            <div className="flex flex-col gap-4">
                              <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mb-2">Contact Centre</h4>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Omini Channel Contact Center</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Managed Cloud Contact Centre</a>
                            </div>
                            {/* Column 4 */}
                            <div className="flex flex-col gap-4">
                              <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mb-2">Voice Loggers</h4>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Missed Call Manager</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">SIP Voice Logger</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Analog Voice Logger</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">PRI Voice Logger</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Digital Voice Logger</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Call Billing Software</a>
                              {/* <a href="#" className="text-sm text-gray-400 dark:text-gray-500 hover:text-brand-red transition-colors underline mt-2">See All</a> */}
                            </div>
                            {/* Column 5 */}
                            <div className="flex flex-col gap-4">
                              <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mb-2">Microsoft Teams Telephony</h4>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Microsoft Teams Telephony Setup</a>
                              <a href="#" className="text-sm text-gray-700 dark:text-gray-300 hover:text-brand-red transition-colors">Microsoft Teams Direct Routing</a>
                            </div>
                            {/* Column 6 */}
                            <div className="flex flex-col gap-4">
                              <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mb-2">Telephony PMS Integration Gateway</h4>
                            </div>
                            {/* Column 7 */}
                            <div className="flex flex-col gap-4">
                              <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mb-2">SIP Trunks</h4>
                            </div>
                          </div>
                          
                          {/* Bottom Bar */}
                          <div className="flex flex-col md:flex-row">
                            <div className="flex-1 bg-gray-100 dark:bg-[#1a1a1a] p-8 flex items-center gap-12">
                              <a href="/service" onClick={(e) => handleLinkClick(e, 'Service', '/service')} className="text-sm font-medium text-gray-900 dark:text-white hover:text-brand-red flex items-center gap-2 group/link">
                                View all products (A-Z) 
                                <span className="w-5 h-5 rounded-full bg-brand-red text-white flex items-center justify-center text-[10px] group-hover/link:translate-x-1 transition-transform">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                                </span>
                              </a>
                              <a href="/about" onClick={(e) => handleLinkClick(e, 'About', '/about')} className="text-sm font-medium text-gray-900 dark:text-white hover:text-brand-red flex items-center gap-2 group/link">
                                View Neotronics Practices 
                                <span className="w-5 h-5 rounded-full bg-brand-red text-white flex items-center justify-center text-[10px] group-hover/link:translate-x-1 transition-transform">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                                </span>
                              </a>
                            </div>
                            <div className="bg-brand-red p-8 flex items-center justify-center md:justify-start md:w-1/3">
                              <a href="/contact" onClick={(e) => handleLinkClick(e, 'Contact', '/contact')} className="text-white font-medium flex items-center gap-2 group/link">
                                Connect with us 
                                <span className="w-5 h-5 rounded-full bg-white text-brand-red flex items-center justify-center text-[10px] group-hover/link:translate-x-1 transition-transform">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                                </span>
                              </a>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            const isActive = link.name.toLowerCase() === currentView;

            // if (link.name === 'Blogs') {
            //   return (
            //     <a
            //       key={link.name}
            //       href={link.href}
            //       className={`text-sm font-medium transition-colors ${
            //         isActive 
            //           ? 'text-brand-red' 
            //           : 'text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white'
            //       }`}
            //     >
            //       {link.name}
            //     </a>
            //   );
            // }

            return (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.name, link.href)}
                className={`text-sm font-medium transition-colors ${
                  isActive 
                    ? 'text-brand-red' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white'
                }`}
              >
                {link.name}
              </a>
            );
          })}
        </nav>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-4">
           {/* Theme Toggle */}
           <button 
             onClick={toggleTheme}
             className="p-2 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
             aria-label="Toggle Theme"
           >
             {isDark ? <Sun size={20} /> : <Moon size={20} />}
           </button>

          <button 
            onClick={handleContactClick}
            className="px-6 py-2.5 text-xs font-semibold tracking-wide text-white bg-gradient-to-b from-[#5c1010] to-black border border-[#8a1c1c] rounded-full hover:shadow-[0_0_20px_rgba(220,38,38,0.4)] hover:border-brand-red transition-all duration-300 transform hover:-translate-y-0.5"
          >
            Get In Touch
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex items-center gap-4">
           <button 
             aria-label="toggle-theme"
             onClick={toggleTheme}
             className="p-2 rounded-full text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
           >
             {isDark ? <Sun size={20} /> : <Moon size={20} />}
           </button>

          <button
            aria-label="toggle-menu"
            className="text-gray-900 dark:text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: '100vh' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 dark:bg-black/95 border-b border-gray-200 dark:border-white/10 overflow-y-auto absolute w-full top-full left-0 pb-24"
          >
            <div className="px-6 py-8 flex flex-col gap-6 items-center w-full min-h-min">
              {navLinks.map((link) => {
                const isActive = link.name.toLowerCase() === currentView || (link.name === 'Service' && currentView === 'service');
                
                if (link.name === 'Service') {
                  return (
                    <div key={link.name} className="flex flex-col items-center w-full">
                      <div className="flex items-center gap-2">
                        <a
                          href={link.href}
                          onClick={(e) => handleLinkClick(e, link.name, link.href)}
                          className={`text-lg font-medium transition-colors ${
                            isActive
                              ? 'text-brand-red'
                              : 'text-gray-800 dark:text-gray-300 hover:text-brand-red dark:hover:text-brand-red'
                          }`}
                        >
                          {link.name}
                        </a>
                        <button
                          onClick={() => setMobileServiceOpen(!mobileServiceOpen)}
                          className={`p-2 transition-colors ${
                            isActive ? 'text-brand-red' : 'text-gray-500 hover:text-brand-red'
                          }`}
                        >
                          <motion.div
                            animate={{ rotate: mobileServiceOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </motion.div>
                        </button>
                      </div>
                      
                      <AnimatePresence>
                        {mobileServiceOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden w-full"
                          >
                            <div className="flex flex-col items-center gap-6 py-6 bg-gray-50/50 dark:bg-white/5 rounded-xl mt-2 w-full px-4">
                              {/* Category 1 */}
                              <div className="flex flex-col items-center gap-3 w-full">
                                <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider">AI IPBX</h4>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">EPABX</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">IP Phones</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">IP PBX</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">FXS Gateway</a>
                              </div>
                              
                              <div className="w-1/2 h-px bg-gray-200 dark:bg-white/10"></div>

                              {/* Category 2 */}
                              <div className="flex flex-col items-center gap-3 w-full">
                                <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider">Cloud Telephony</h4>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Cloud PBX</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Mobile Call Analytics</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Multi-Cloud Networking</a>
                              </div>

                              <div className="w-1/2 h-px bg-gray-200 dark:bg-white/10"></div>

                              {/* Category 3 */}
                              <div className="flex flex-col items-center gap-3 w-full">
                                <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider">Contact Centre</h4>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Omini Channel Contact Center</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Managed Cloud Contact Centre</a>
                              </div>

                              <div className="w-1/2 h-px bg-gray-200 dark:bg-white/10"></div>

                              {/* Category 4 */}
                              <div className="flex flex-col items-center gap-3 w-full">
                                <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider">Voice Loggers</h4>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Missed Call Manager</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">SIP Voice Logger</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Analog Voice Logger</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">PRI Voice Logger</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Digital Voice Logger</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Call Billing Software</a>
                              </div>

                              <div className="w-1/2 h-px bg-gray-200 dark:bg-white/10"></div>

                              {/* Category 5 */}
                              <div className="flex flex-col items-center gap-3 w-full text-center">
                                <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider">Microsoft Teams Telephony</h4>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Microsoft Teams Telephony Setup</a>
                                <a href="#" className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-red transition-colors">Microsoft Teams Direct Routing</a>
                              </div>

                              <div className="w-1/2 h-px bg-gray-200 dark:bg-white/10"></div>

                              {/* Category 6 & 7 */}
                              <div className="flex flex-col items-center gap-3 w-full text-center">
                                <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider">Telephony PMS Integration Gateway</h4>
                                <h4 className="text-xs font-bold text-brand-red uppercase tracking-wider mt-2">SIP Trunks</h4>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                // if (link.name === 'Blogs') {
                //   return (
                //     <a
                //       key={link.name}
                //       href={link.href}
                //       className={`text-lg font-medium transition-colors ${
                //         isActive
                //           ? 'text-brand-red'
                //           : 'text-gray-800 dark:text-gray-300 hover:text-brand-red dark:hover:text-brand-red'
                //       }`}
                //     >
                //       {link.name}
                //     </a>
                //   );
                // }

                return (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link.name, link.href)}
                    className={`text-lg font-medium transition-colors ${
                      isActive || (link.name === 'Blogs' && currentView === 'blog')
                        ? 'text-brand-red'
                        : 'text-gray-800 dark:text-gray-300 hover:text-brand-red dark:hover:text-brand-red'
                    }`}
                  >
                    {link.name}
                  </a>
                );
              })}
              <button 
                onClick={handleContactClick}
                className="w-full py-3 text-center text-white bg-gradient-to-b from-[#5c1010] to-black border border-[#8a1c1c] rounded-lg"
              >
                Get In Touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;